import { useEffect, useRef, useState, useCallback } from "react";
import type { Snapshot, WireEvent, Lane, Gate } from "./types";

export function useWarRoomState() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [lastEventTs, setLastEventTs] = useState<number>(Date.now());
  const [wsState, setWsState] = useState<"connecting" | "open" | "closed">("connecting");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | null>(null);

  const hydrate = useCallback(async () => {
    try {
      const r = await fetch("/api/state", { credentials: "include" });
      if (!r.ok) return;
      const data = (await r.json()) as Snapshot;
      setSnap(data);
      setLastEventTs(Date.now());
    } catch {
      /* aggregator unreachable, leave empty */
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${proto}//${window.location.host}/ws`);
    wsRef.current = ws;
    setWsState("connecting");
    ws.onopen = () => setWsState("open");
    ws.onmessage = (ev) => {
      setLastEventTs(Date.now());
      let msg: WireEvent;
      try { msg = JSON.parse(ev.data) as WireEvent; } catch { return; }
      if (msg.type === "heartbeat" || msg.type === "hello") return;
      setSnap((prev) => prev ? applyEvent(prev, msg) : prev);
    };
    ws.onclose = () => {
      wsRef.current = null;
      setWsState("closed");
      if (reconnectRef.current === null) {
        reconnectRef.current = window.setTimeout(() => {
          reconnectRef.current = null;
          connect();
        }, 2000);
      }
    };
    ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
  }, []);

  useEffect(() => {
    hydrate();
    connect();
    const t = window.setInterval(() => {
      // Force re-hydrate if no events for 60s
      if (Date.now() - lastEventTs > 60_000) hydrate();
    }, 15_000);
    return () => {
      window.clearInterval(t);
      if (wsRef.current) try { wsRef.current.close(); } catch { /* ignore */ }
      if (reconnectRef.current !== null) window.clearTimeout(reconnectRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { snap, lastEventTs, wsState, refresh: hydrate };
}

function applyEvent(prev: Snapshot, ev: WireEvent): Snapshot {
  switch (ev.type) {
    case "lane_state": {
      // War Room v0.5 — merge server-pushed lane state into the lane map.
      // Match by case-insensitive name, since the ingest pipe is name-keyed.
      const byName = new Map(ev.lanes.map((l) => [l.name.toLowerCase(), l]));
      const lanes: Lane[] = prev.fleet.lanes.map((l) => {
        const incoming = byName.get(l.name.toLowerCase()) ?? byName.get(l.id.toLowerCase());
        if (!incoming) return l;
        return {
          ...l,
          status: incoming.status as Lane["status"],
          colour: incoming.colour,
          ageMinutes: incoming.age_minutes,
          lastOutputExcerpt: incoming.last_output_excerpt,
          queueDepth: incoming.queue_depth,
          last_action_ts: new Date(incoming.last_active_at).toISOString(),
        };
      });
      return { ...prev, fleet: { ...prev.fleet, lanes } };
    }
    case "lane.event": {
      // No snapshot mutation today; events are surfaced via the war room
      // feed once the aggregator wires them up. Additive-only.
      return prev;
    }
    case "lane.status": {
      const lanes: Lane[] = prev.fleet.lanes.map((l) =>
        l.id === ev.payload.lane_id
          ? { ...l, status: ev.payload.status, last_action_ts: ev.payload.last_action_ts, last_action_summary: ev.payload.last_action_summary }
          : l,
      );
      return { ...prev, fleet: { ...prev.fleet, lanes } };
    }
    case "gate.event": {
      const gates: Gate[] = prev.veyra.gates.map((g) =>
        g.slug === ev.payload.slug ? { ...g, ...ev.payload } as Gate : g,
      );
      return { ...prev, veyra: { ...prev.veyra, gates } };
    }
    case "gate.created": {
      const exists = prev.veyra.gates.some((g) => g.slug === ev.payload.slug);
      const gates = exists ? prev.veyra.gates : [...prev.veyra.gates, ev.payload];
      return { ...prev, veyra: { ...prev.veyra, gates } };
    }
    case "war_room.message": {
      const incoming = ev.payload;

      // Detect handoff destination: look for "→ <lanename>" in message body.
      const handoffMatch = incoming.message.match(/→\s*([a-zA-Z]+)/i);
      const handoff_to = handoffMatch ? handoffMatch[1].toLowerCase() : undefined;

      // Dedupe: check if the most recent message from the same lane has a
      // similar body. "Similar" = first 40 chars match after stripping
      // punctuation and numbers (case-insensitive).
      const normalise = (s: string) =>
        s.replace(/[^a-z\s]/gi, "").toLowerCase().slice(0, 40).trim();

      const lastSameLane = prev.war_room.find((m) => m.lane === incoming.lane);
      if (lastSameLane && normalise(lastSameLane.message) === normalise(incoming.message)) {
        // Same pattern — update ts and increment count in place.
        const updated = prev.war_room.map((m) => {
          if (m === lastSameLane) {
            return { ...m, ts: incoming.ts, count: (m.count ?? 1) + 1 };
          }
          return m;
        });
        return { ...prev, war_room: updated };
      }

      // Different pattern — prepend as new entry.
      const entry = { ...incoming, ...(handoff_to ? { handoff_to } : {}) };
      return { ...prev, war_room: [entry, ...prev.war_room].slice(0, 200) };
    }
    case "inbox.item": {
      return { ...prev, inbox: [ev.payload, ...prev.inbox] };
    }
    case "todo.added": {
      return { ...prev, frankie_todos: [ev.payload, ...prev.frankie_todos] };
    }
    case "todo.completed": {
      return { ...prev, frankie_todos: prev.frankie_todos.filter((t) => t.id !== ev.payload.id) };
    }
    default:
      return prev;
  }
}

export async function dispatchTask(lane: string, task: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const r = await fetch("/api/dispatch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ lane, task }),
    });
    return (await r.json()) as { ok: boolean; error?: string };
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}
